import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function Books() {
  const { t } = useI18n();
  const { addBook } = useCart();
  const { user } = useAuth();
  const [books, setBooks] = useState(null);

  useEffect(() => { api.get("/books").then(({ data }) => setBooks(data)).catch(() => setBooks([])); }, []);

  const openLoginIfNeeded = () => { if (!user) { window.location.href = "/login"; return false; } return true; };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <h1 className="font-serif text-4xl sm:text-5xl text-[#5C061C]">{t("books.title")}</h1>
        <p className="mt-2 text-[#6E6259] max-w-2xl">{t("books.subtitle")}</p>
      </div>

      {!books ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3].map(i => <Skeleton key={i} className="h-96 rounded-2xl" />)}
        </div>
      ) : books.length === 0 ? (
        <p className="text-[#6E6259]">No books available yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {books.map((b) => (
            <div key={b.id} className="card-warm rounded-2xl overflow-hidden flex flex-col" data-testid={`book-card-${b.id}`}>
              <div className="relative aspect-[3/4] bg-[#F4EEDF]">
                {b.cover_url ? <img src={b.cover_url} alt={b.title} className="w-full h-full object-cover" /> : <BookOpen className="h-16 w-16 m-auto mt-24 text-[#B45309]" />}
                <Badge className="absolute top-3 right-3 bg-[#5C061C] text-white uppercase text-[10px]">{b.language}</Badge>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-serif text-xl text-[#5C061C]">{b.title}</h3>
                {b.author && <p className="text-sm text-[#6E6259] mt-1">{t("books.by")} {b.author}</p>}
                <p className="text-sm text-[#1F1A17]/80 mt-2 line-clamp-2">{b.description}</p>
                <div className="mt-4 space-y-2">
                  {b.durations?.map((d) => (
                    <button
                      key={d.id}
                      onClick={() => { if (openLoginIfNeeded()) { addBook(b, d); toast.success(`${b.title} • ${d.label} added`); } }}
                      data-testid={`book-rent-${b.id}-${d.days}days`}
                      className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg border border-[#E8DEC8] bg-white hover:bg-[#F4EEDF] transition"
                    >
                      <span className="text-sm font-medium text-[#1F1A17]">{d.label}</span>
                      <span className="text-[#D97706] font-semibold">₹{d.price}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
